import { z } from 'zod';

export type Network =
  | 'mainnet'
  | 'rinkeby'
  | 'goerli'
  | 'polygon'
  | 'mumbai'
  | 'arbitrum'
  | 'optimism'
  | 'gnosis'
  | 'tezos';

export interface User {
  id: string;
  preferredUsername?: string;
  accounts?: string[];
  email?: string;
  emailVerified?: boolean;
  phoneNumber?: string;
  phoneNumberVerified?: boolean;
}

export interface Profile {
  id: string;
  type: 'CREATOR' | 'COLLECTOR';
  username: string;
  name: string;
  imageUrl?: string;
  coverImageUrl?: string;
  description: string;
  links: { website?: string; twitter?: string; instagram?: string };
  connectedAccounts: Account[];
}

export interface Account {
  id: string;
  externalId: string;
  public: boolean;
}

export interface CardOnFile {
  paymentMethod: { id: string; cardLast4: string; cardBrand: string };
}

export interface Payment {
  id: string;
  createdAt: string;
  currency: string;
  paymentMethodId: string;
  paymentMethodDescription: string;
  subtotal: string;
  fees: string;
  taxes: string;
  total: string;
  processorPaymentSecret?: string;
  priceId: string;
  tokens: StripeToken[];
}

export interface PaymentAction {
  nextAction: {
    type: string;
  };
}

export interface PaymentMethod {
  id: string;
  cardLast4: string;
  cardBrand: string;
}

export interface StripeToken {
  id: string;
  state: string;
}

export const enum SaleType {
  LimitedEdition = 'LIMITED_EDITION',
  OpenEdition = 'OPEN_EDITION',
  OffChainAuction = 'OFF_CHAIN_AUCTION',
  OnChainAuction = 'ON_CHAIN_AUCTION',
}

interface LegacyTokenMetadata extends TokenMetadata {
  animation_url?: string;
  youtube_url?: string;
  external_url?: string;
  background_color?: string;
  marketplace_url?: string;
}

interface ProductBase {
  id: string;
  saleType: SaleType;
  username: string;
  network: Network;
  tokenContractAddress: string;
  tokenMetadata: LegacyTokenMetadata;
  totalAvailable?: number;
  totalSold: number;
  prices: Price[];
  auctions: Auction[];
  gatedContent: string[];
  purchaseLink: string;
  liveAt: string;
  salesEndAt?: string | null;
  previewAt?: string | null;
  createdAt: string;
  isGated?: boolean;
}

export interface LimitedEditionProduct extends ProductBase {
  saleType: SaleType.LimitedEdition;
  totalAvailable: number;
  auctions: [];
}

export interface OpenEditionProduct extends ProductBase {
  saleType: SaleType.OpenEdition;
  salesEndAt: string;
  auctions: [];
}

export interface OffChainAuctionProduct extends ProductBase {
  saleType: SaleType.OffChainAuction;
  salesEndAt: string;
  totalAvailable: number;
  auctions: [Auction];
}

export interface OnChainAuctionProduct extends ProductBase {
  saleType: SaleType.OnChainAuction;
  salesEndAt: string;
  totalAvailable: number;
  auctions: [Auction];
}

export type Product =
  | LimitedEditionProduct
  | OpenEditionProduct
  | OffChainAuctionProduct
  | OnChainAuctionProduct;

export interface Price {
  id: string;
  currency: string;
  price: string;
}

export interface AuctionInfo {
  bids: OffChainBid[] | OnChainBid[];
  ended: boolean;
  minBid: string;
  automaticSettlement: boolean;
}

export interface Auction {
  id: string;
  startsAt: string;
  endsAt: string;
  status: string;
  tokenId?: string;
  tokenContractId?: string;
  auctionContractId?: string;
  info: AuctionInfo;
}

interface BidBase {
  id?: string;
  createdAt: Date | string;
  bidder?: string;
  currency?: string;
  amount: number | string;
  transactionHash?: string;
  username?: string;
}

export interface OffChainBid extends BidBase {
  id: string;
  createdAt: string;
  username?: string;
  currency: string;
  amount: number;
}

export interface OnChainBid extends BidBase {
  auctionId: string;
  bidder: string;
  amount: string;
  transactionHash: string;
  createdAt: Date;
}

export type Bid = OffChainBid | OnChainBid;

export type TokenMetadata = z.infer<typeof TokenMetadata>;
export const TokenMetadata = z.object({
  name: z.string(),
  image: z.string().nullish(),
  description: z.string().nullish(),
  animationUrl: z.string().nullish(),
  youtubeUrl: z.string().nullish(),
  quantity: z.number().nullish(),
  externalUrl: z.string().nullish(),
  hasRarityData: z.boolean().nullish(),
  backgroundColor: z.string().nullish(),
  marketplaceUrl: z.string().nullish(),
  attributes: z.array(z.record(z.unknown())).nullish(),
  properties: z.record(z.unknown()).nullish(),
});

export type Token = z.infer<typeof Token>;
export const Token = z.object({
  id: z.string(),
  contractAddress: z.string(),
  coinType: z.number(),
  chainId: z.number(),
  metadata: TokenMetadata,
});

export enum ContractStandard {
  ERC20 = 'ERC20',
  ERC721 = 'ERC721',
  ERC1155 = 'ERC1155',
  ERC2981 = 'ERC2981',
  ERC4973 = 'ERC4973',
  Unspecified = 'UNSPECIFIED',
}

export enum PlatformCategory {
  None = 'NONE',
  CurrencyDex = 'CURRENCY_DEX',
  CurrencyCex = 'CURRENCY_CEX',
  NftMarketplace = 'NFT_MARKETPLACE',
}

export type ContractSocial = z.infer<typeof ContractSocial>;
export const ContractSocial = z.object({
  telegramUrl: z.string().nullish(),
  twitterUsername: z.string().nullish(),
  instagramUsername: z.string().nullish(),
  wikiUrl: z.string().nullish(),
  mediumUsername: z.string().nullish(),
  discordUrl: z.string().nullish(),
  externalUrl: z.string().nullish(),
});

export type OpenseaFees = z.infer<typeof OpenseaFees>;
export const OpenseaFees = z.object({
  sellerFees: z.record(z.number()),
  openseaFees: z.record(z.number()),
});

export type ContractMetadata = z.infer<typeof ContractMetadata>;
export const ContractMetadata = z.object({
  updatedAt: z.string().nullish(),
  name: z.string().nullish(),
  description: z.string().nullish(),
  image: z.string().nullish(),
  externalLink: z.string().nullish(),
  version: z.string().nullish(),
  openseaSlug: z.string().nullish(),
  isOpenseaVerified: z.boolean().nullish(),
  isEtherscanVerified: z.boolean().nullish(),
  social: ContractSocial.nullish(),
  openseaFees: OpenseaFees.nullish(),
  category: z.nativeEnum(PlatformCategory).nullish(),
});

export type Contract = z.infer<typeof Contract>;
export const Contract = z.object({
  contractAddress: z.string(),
  chainId: z.number(),
  coinType: z.number(),
  name: z.string().nullish(),
  symbol: z.string().nullish(),
  decimals: z.number().nullish(),
  standards: z.array(z.nativeEnum(ContractStandard)),
  tokenStandard: z.nativeEnum(ContractStandard),
  tokenBaseUri: z.string().nullish(),
  collection: ContractMetadata,
  deployBlock: z.number().nullish(),
  deployTxHash: z.string().nullish(),
});

export const enum ClaimableType {
  Raffle = 'RAFFLE',
  DropLinkGroup = 'DROP_LINK_GROUP',
}

export interface ClaimCode {
  id: string;
  code: string;
  description?: string;
  maxClaims: number;
  storeUsername: string;
  claimableType: ClaimableType;
}

// SvelteKit types
export interface Page {
  url: URL;
  params: Record<string, string>;
  status: number;
  error: Error | null;
}

export interface Store {
  id: string;
  application: string;
  countryCode: string;
  title: string;
  description: string;
  baseUrl: string;
  processor: StoreProcessor;
  processorAccountId: string;
  processorApiKey: string;
  processorApiSecret: string;
  processorLogoUri?: string;
  backgroundImageUri?: string;
  themeUrl?: string;
  enterpriseWalletCredentialId?: string;
  enterpriseWalletCredentialSecret?: string;
  processorStatus?: string;
  plan?: string;
  subscription?: Subscription;
  socialLinks: Record<string, string> | null;
}

export interface Subscription {
  id: string;
  paymentMethodType?: string;
  paymentMethodIdentifier?: string;
  level: SubscriptionLevel;
}

export type SubscriptionLevel = 'unknown' | 'creator' | 'creator_pro' | 'agency';

export const enum StoreProcessor {
  Stripe = 'stripe',
  StripeTest = 'stripe_test',
}

export const enum SignInWithEthSetting {
  Default = 'DEFAULT',
  Disabled = 'DISABLED',
  RequireShadowAccount = 'REQUIRE_SHADOW_ACCOUNT',
  RequireFullAccount = 'REQUIRE_FULL_ACCOUNT',
}

export interface ClaimCodeTheme {
  id: string;
  appId: string;

  logoUrl?: string;
  backgroundImageUrl?: string;

  primaryTextColor?: string;
  secondaryTextColor?: string;
  tertiaryTextColor?: string;

  primaryBackgroundColor?: string;
  secondaryBackgroundColor?: string;

  primaryButtonColor?: string;
  primaryButtonHoverColor?: string;
  primaryButtonTextColor?: string;

  secondaryButtonColor?: string;
  secondaryButtonHoverColor?: string;
  secondaryButtonTextColor?: string;

  linkColor?: string;
  linkHoverColor?: string;

  progressBarColor?: string;
  progressBarBackgroundColor?: string;
  progressBarBorderColor?: string;
  progressBarActiveTextColor?: string;
  progressBarInactiveTextColor?: string;

  postClaimRedirectButtonUrl?: string;
  postClaimRedirectButtonText?: string;
  footerHtml?: string;
  signInWithEthSetting?: SignInWithEthSetting;
}

export interface Gate {
  id: string;
  createdAt: string;
  name: string;
  description?: string;
  image?: string;
  isActive: boolean;
  isPublished: boolean;
}

export interface TokenSaleActivityEvent {
  type: string;
  contractAddress: string;
  tokenId: string;
  paymentCurrency: string;
  paymentAmount: string;
  seller: string;
  buyer: string;
}

export interface TokenTransferActivityEvent {
  type: string;
  contractAddress: string;
  tokenId: string;
  fromAddress: string;
  toAddress: string;
}

export interface ContractInteractionActivityEvent {
  type: string;
  contractAddress: string;
}

export interface Activity {
  timestamp: string;
  chainId: number;
  coinType: number;
  transactionHash: string;
  events: (
    | TokenSaleActivityEvent
    | TokenTransferActivityEvent
    | ContractInteractionActivityEvent
  )[];
}

export interface Activity {
  timestamp: string;
  chainId: number;
  coinType: number;
  transactionHash: string;
  events: (
    | TokenSaleActivityEvent
    | TokenTransferActivityEvent
    | ContractInteractionActivityEvent
  )[];
}

export interface ActivityStatistics {
  associatedAddress: string;
  totalMints: number;
  totalPurchases: number;
  totalSales: number;
}
